import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PRODUCTS, SOLUTIONS, anchorFor } from './catalog';

const RESOURCES = [
  { id: 'about', label: 'About RiskLo', short: 'Who we are and why we built it.' },
  { id: 'help', label: 'FAQ', short: 'Answers about plans, data and accounts.' },
  { id: 'contact', label: 'Contact', short: 'Talk to the team.' },
];

/** Go to a marketing page, then scroll to a section on it once it renders. */
export function goToSection(onNavigate, page, anchor) {
  if (onNavigate) onNavigate(page);
  if (!anchor) return;
  [80, 250, 600].forEach((ms) => setTimeout(() => {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: ms === 80 ? 'auto' : 'smooth', block: 'start' });
  }, ms));
}

function Chevron() {
  return (
    <svg className="mnav-chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function Tag({ tag }) {
  if (!tag) return null;
  return <span className={`mnav-tag mnav-tag-${tag.toLowerCase()}`}>{tag}</span>;
}

function ProductsMenu({ onPick }) {
  return (
    <div className="mnav-panel mnav-panel-products" role="menu">
      <div className="mnav-products">
        {PRODUCTS.map((p) => (
          <button key={p.id} type="button" role="menuitem" className="mnav-item" onClick={() => onPick('features', anchorFor('product', p.id))}>
            <span className={`mnav-icon tone-${p.tone}`}>{p.icon}</span>
            <span className="mnav-item-text">
              <span className="mnav-item-title">{p.title} <Tag tag={p.tag} /></span>
              <span className="mnav-item-short">{p.short}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="mnav-panel-foot">
        <button type="button" className="mnav-foot-link" onClick={() => onPick('features')}>See every feature →</button>
        <button type="button" className="mnav-foot-link" onClick={() => onPick('pricing')}>Compare plans →</button>
      </div>
    </div>
  );
}

function SolutionsMenu({ onPick }) {
  return (
    <div className="mnav-panel mnav-panel-solutions" role="menu">
      <div className="mnav-panel-label">By trader type</div>
      {SOLUTIONS.map((s) => (
        <button
          key={s.id}
          type="button"
          role="menuitem"
          className="mnav-item"
          onClick={() => (s.page ? onPick(s.page) : onPick('features', anchorFor('solution', s.id)))}
        >
          <span className={`mnav-icon tone-${s.tone}`}>{s.icon}</span>
          <span className="mnav-item-text">
            <span className="mnav-item-title">{s.title}</span>
            <span className="mnav-item-short">{s.short}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function ResourcesMenu({ onPick }) {
  return (
    <div className="mnav-panel mnav-panel-resources" role="menu">
      {RESOURCES.map((r) => (
        <button key={r.id} type="button" role="menuitem" className="mnav-item is-plain" onClick={() => onPick(r.id)}>
          <span className="mnav-item-text">
            <span className="mnav-item-title">{r.label}</span>
            <span className="mnav-item-short">{r.short}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

const MENUS = [
  { id: 'products', label: 'Products', Panel: ProductsMenu, pages: ['features'] },
  { id: 'solutions', label: 'Solutions', Panel: SolutionsMenu, pages: [] },
  { id: 'pricing', label: 'Pricing', page: 'pricing' },
  { id: 'companies', label: 'For Companies', page: 'companies' },
  { id: 'resources', label: 'Resources', Panel: ResourcesMenu, pages: ['about', 'help', 'contact'] },
];

function MarketingNav({ user, currentPage, onNavigate, onPrimaryCta }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileSection, setMobileSection] = useState(null);
  const navRef = useRef(null);
  const hoverTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [currentPage]);

  // Close an open menu on Escape or a click outside the nav.
  useEffect(() => {
    if (!openMenu) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    const onDown = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [openMenu]);

  const pick = useCallback((page, anchor) => {
    setMobileOpen(false);
    setOpenMenu(null);
    goToSection(onNavigate, page, anchor);
  }, [onNavigate]);

  const hoverOpen = (id) => { clearTimeout(hoverTimer.current); setOpenMenu(id); };
  const hoverClose = () => { clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => setOpenMenu(null), 160); };

  const isSignedIn = Boolean(user?.email);

  return (
    <nav ref={navRef} className={`marketing-nav ${scrolled ? 'is-scrolled' : ''}`} aria-label="Primary">
      <div className="marketing-nav-inner">
        <button
          type="button"
          className="marketing-brand"
          onClick={() => pick('home')}
          aria-label="Go to RiskLo home"
        >
          <span className="marketing-brand-mark" aria-hidden="true">
            <img src="/RiskLo_LogoOnly.png" alt="" />
          </span>
          <span className="marketing-brand-text">RiskLo<sup className="reg-mark">®</sup></span>
        </button>

        <div className="marketing-nav-links">
          {MENUS.map((m) => {
            if (!m.Panel) {
              return (
                <button key={m.id} type="button" className={currentPage === m.page ? 'is-active' : ''} onClick={() => pick(m.page)}>
                  {m.label}
                </button>
              );
            }
            const open = openMenu === m.id;
            const { Panel } = m;
            return (
              <div key={m.id} className="mnav-wrap" onMouseEnter={() => hoverOpen(m.id)} onMouseLeave={hoverClose}>
                <button
                  type="button"
                  className={`mnav-trigger${open ? ' is-open' : ''}${m.pages.includes(currentPage) ? ' is-active' : ''}`}
                  aria-expanded={open}
                  aria-haspopup="true"
                  onClick={() => setOpenMenu(open ? null : m.id)}
                >
                  {m.label} <Chevron />
                </button>
                {open ? <Panel onPick={pick} /> : null}
              </div>
            );
          })}
        </div>

        <div className="marketing-nav-cta">
          {isSignedIn ? (
            <>
              <button type="button" className="marketing-btn-link marketing-nav-cta-desktop" onClick={() => pick('account')}>
                Account
              </button>
              <button
                type="button"
                className="marketing-btn marketing-nav-cta-desktop"
                onClick={() => (onPrimaryCta ? onPrimaryCta() : pick('results'))}
              >
                Open dashboard
              </button>
            </>
          ) : (
            <>
              <button type="button" className="marketing-btn-link marketing-nav-cta-desktop" onClick={() => pick('login')}>
                Sign in
              </button>
              <button
                type="button"
                className="marketing-btn marketing-nav-cta-desktop"
                onClick={() => (onPrimaryCta ? onPrimaryCta() : pick('login'))}
              >
                Get started
              </button>
            </>
          )}

          <button
            type="button"
            className="marketing-nav-toggle"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className={`marketing-nav-mobile-panel ${mobileOpen ? 'is-open' : ''}`}>
        {MENUS.map((m) => {
          if (!m.Panel) {
            return <button key={m.id} type="button" onClick={() => pick(m.page)}>{m.label}</button>;
          }
          const open = mobileSection === m.id;
          const { Panel } = m;
          return (
            <div key={m.id} className="mnav-mobile-group">
              <button type="button" className={`mnav-mobile-trigger${open ? ' is-open' : ''}`} aria-expanded={open} onClick={() => setMobileSection(open ? null : m.id)}>
                {m.label} <Chevron />
              </button>
              {open ? <div className="mnav-mobile-body"><Panel onPick={pick} /></div> : null}
            </div>
          );
        })}
        <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
          {isSignedIn ? (
            <>
              <button type="button" className="marketing-btn-ghost" style={{ width: '100%' }} onClick={() => pick('account')}>
                Account
              </button>
              <button
                type="button"
                className="marketing-btn"
                style={{ width: '100%' }}
                onClick={() => (onPrimaryCta ? onPrimaryCta() : pick('results'))}
              >
                Open dashboard
              </button>
            </>
          ) : (
            <>
              <button type="button" className="marketing-btn-ghost" style={{ width: '100%' }} onClick={() => pick('login')}>
                Sign in
              </button>
              <button
                type="button"
                className="marketing-btn"
                style={{ width: '100%' }}
                onClick={() => (onPrimaryCta ? onPrimaryCta() : pick('login'))}
              >
                Get started
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export default MarketingNav;
